import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import PaginationComponent from '../components/PaginationComponent';
import { validateSession } from '../utils/functions';
import { printComanda } from '../utils/printComanda';
import { buildPaginationQuery, getPaginatedData } from '../utils/pagination';

var config = process.env;

// ENI-96: cada estado con su color, para leer la cola de un vistazo (no por texto).
// pending=amarillo, ready=verde, delivered=celeste, cancelled=rojo.
const STATUS_META = {
    pending:   { label: 'Pendiente', bg: '#ffc107', fg: '#212529' },
    ready:     { label: 'Lista',     bg: '#28a745', fg: '#ffffff' },
    delivered: { label: 'Entregada', bg: '#17a2b8', fg: '#ffffff' },
    cancelled: { label: 'Anulada',   bg: '#dc3545', fg: '#ffffff' },
};

// ENI-100: filtros de la cola (el valor viaja como ?status= al backend).
const STATUS_FILTERS = [
    { value: 'all', label: 'Todas' },
    { value: 'active', label: 'Activas' },
    { value: 'delivered', label: 'Entregadas' },
    { value: 'cancelled', label: 'Anuladas' },
];

const PAGE_SIZE = 25;

function authHeaders() {
    return { 'Content-Type': 'application/json', 'access-token': localStorage.getItem('token') };
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || { label: status, bg: '#e9ecef', fg: '#212529' };
    return (
        <span style={{
            background: meta.bg, color: meta.fg, padding: '3px 10px',
            borderRadius: '12px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
        }}>{meta.label}</span>
    );
}

class CafeteriaOrdersPage extends Component {
    constructor() {
        super();
        this.state = {
            checked: false, role: 0, orders: [], loading: true, reprintingId: null,
            currentPage: 1, quantityShowRows: PAGE_SIZE, totalItems: 0, statusFilter: 'all',
            cancellingId: null, // ENI-193: venta cuya anulación está preguntando por los insumos
            cancellingDelivered: false, // la comanda de esa venta ya estaba Entregada
        };
    }

    async componentDidMount() {
        const role = await validateSession();
        this.setState({ checked: true, role });
        this.fetchOrders();
    }

    fetchOrders = ({ page = this.state.currentPage, status = this.state.statusFilter } = {}) => {
        this.setState({ loading: true });
        const query = buildPaginationQuery({
            page,
            limit: this.state.quantityShowRows,
            sortBy: 'created_at',
            sortDir: 'desc',
            extraParams: status && status !== 'all' ? { status } : {},
        });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'cafeteria/kitchen-orders?' + query, { headers: authHeaders() })
            .then((r) => r.json())
            .then((data) => {
                if (data.error > 0) {
                    alert(data.message);
                    this.setState({ loading: false });
                    return;
                }
                const p = getPaginatedData(data);
                // Si la página pedida quedó fuera de rango (p.ej. entregar/anular el último
                // ítem de la última página), el backend clampa la metadata pero devuelve
                // items:[] con total>0. Re-pedimos la última página real para no mostrar la
                // cola vacía mientras el paginador dice "de N items".
                if ((p.items || []).length === 0 && (p.total || 0) > 0 && (p.page || 1) < page) {
                    this.fetchOrders({ page: p.page || 1, status });
                    return;
                }
                this.setState({
                    orders: p.items || [],
                    currentPage: p.page || page,
                    totalItems: p.total || 0,
                    quantityShowRows: p.limit || this.state.quantityShowRows,
                    loading: false,
                });
            })
            // Sin catch, un rechazo de red/JSON dejaba loading en true para siempre (tabla
            // pegada en "Cargando…"). Resolvemos loading y conservamos los datos previos.
            .catch(() => this.setState({ loading: false }));
    }

    changeFilter = (status) => {
        this.setState({ statusFilter: status, currentPage: 1 });
        this.fetchOrders({ page: 1, status });
    }

    setStatus = (id, status) => {
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/kitchen-order/${id}/status`, {
            method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }),
        })
            .then((r) => r.json())
            .then((data) => { if (data.error > 0) alert(data.message); else this.fetchOrders(); });
    }

    // ENI-193: anular pregunta si los insumos de receta vuelven al stock. Es una decisión
    // del cajero y no del sistema: si la comanda ya se preparó, el café molido no vuelve a
    // la bolsa; si se anula antes de prepararla, sí. Va en un modal de dos botones (una sola
    // pregunta, un solo click) porque en el mostrador dos confirm() encadenados son fricción.
    //
    // También se puede anular una comanda ya Entregada (el pedido que se cargó mal y la cocina
    // ya sacó). Ahí la pregunta suma un aviso: si la venta se cobró, la plata no vuelve sola.
    askCancelSale = (purchaseId, delivered = false) => {
        this.setState({ cancellingId: purchaseId, cancellingDelivered: !!delivered });
    }

    cancelSale = (restoreSupplies) => {
        const purchaseId = this.state.cancellingId;
        this.setState({ cancellingId: null, cancellingDelivered: false });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/sale/${purchaseId}/cancel`, {
            method: 'POST', headers: authHeaders(), body: JSON.stringify({ restoreSupplies }),
        })
            .then((r) => r.json())
            .then((data) => { if (data.error > 0) alert(data.message); else this.fetchOrders(); });
    }

    renderCancelModal = () => {
        if (this.state.cancellingId == null) {
            return null;
        }
        return (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
                <div className="modal-dialog" style={{ marginTop: '10%' }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Anular la venta</h4>
                        </div>
                        <div className="modal-body">
                            <p className="mb-2">Repone el stock de envasados y marca la comanda como anulada. El reintegro de caja se hace aparte.</p>
                            {this.state.cancellingDelivered && (
                                <div className="alert alert-warning py-2 mb-2">
                                    Ya fue entregada. Si se cobró, la caja no se revierte sola: hacé el reintegro a mano.
                                </div>
                            )}
                            <p className="mb-0"><b>¿Los insumos de receta vuelven al stock?</b></p>
                            <small className="text-muted">
                                Si la comanda ya se preparó, no: lo gastado está gastado. Si se anula antes de prepararla, sí.
                            </small>
                        </div>
                        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                            <button type="button" className="btn btn-white" onClick={() => this.setState({ cancellingId: null, cancellingDelivered: false })}>Cancelar</button>
                            <div>
                                <button type="button" className="btn btn-outline-danger mr-2" onClick={() => this.cancelSale(false)}>Anular sin devolver</button>
                                <button type="button" className="btn btn-danger" onClick={() => this.cancelSale(true)}>Anular y devolver insumos</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Guard de doble click: sin él, dos clicks = dos tickets físicos (la térmica no
    // tiene diálogo que serialice) Y dos POST de reprint (print_count inflado).
    reprint = (id) => {
        if (this.state.reprintingId != null) {
            return;
        }
        this.setState({ reprintingId: id });
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + `cafeteria/kitchen-order/${id}/reprint`, {
            method: 'POST', headers: authHeaders(),
        }).catch(() => {});
        Promise.resolve(printComanda(id))
            .finally(() => this.setState({ reprintingId: null }));
    }

    renderFilters = () => {
        return (
            <div className="btn-group btn-group-sm" role="group">
                {STATUS_FILTERS.map((f) => (
                    <button
                        key={f.value}
                        type="button"
                        className={`btn ${this.state.statusFilter === f.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => this.changeFilter(f.value)}
                    >{f.label}</button>
                ))}
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={28} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <HeaderComponent title="Cafetería" subtitle="Comandas" />
                                <div className="d-flex align-items-center">
                                    {this.renderFilters()}
                                    <button type="button" className="btn btn-white ml-2" onClick={() => this.fetchOrders()}>Actualizar</button>
                                </div>
                            </div>
                            <div className="card card-small mb-4">
                                <div className="card-body p-0">
                                    <table className="table mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Destino</th>
                                                <th>Ítems</th>
                                                <th>Estado</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.state.loading && (
                                                <tr><td colSpan="5" className="text-center">Cargando…</td></tr>
                                            )}
                                            {!this.state.loading && this.state.orders.length === 0 && (
                                                <tr><td colSpan="5" className="text-center text-muted">No hay comandas.</td></tr>
                                            )}
                                            {!this.state.loading && this.state.orders.map((o) => (
                                                <tr key={o.id} style={{ borderLeft: `4px solid ${(STATUS_META[o.status] || {}).bg || 'transparent'}` }}>
                                                    <td><strong>#{o.id}</strong></td>
                                                    <td>{o.destination}</td>
                                                    <td>
                                                        {(o.items || []).map((it) => (
                                                            <div key={it.line_no}>
                                                                {it.quantity}x {it.product_name}
                                                                {Number(it.requires_prep) !== 1 ? ' (entregar)' : ''}
                                                                {it.notes ? <span className="text-muted"> — {it.notes}</span> : null}
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td><StatusBadge status={o.status} /></td>
                                                    <td className="text-right">
                                                        {o.status === 'pending' && (
                                                            <button type="button" className="btn btn-sm btn-white" onClick={() => this.setStatus(o.id, 'ready')}>Lista</button>
                                                        )}
                                                        {(o.status === 'pending' || o.status === 'ready') && (
                                                            <button type="button" className="btn btn-sm btn-white ml-1" onClick={() => this.setStatus(o.id, 'delivered')}>Entregada</button>
                                                        )}
                                                        <button type="button" className="btn btn-sm btn-white ml-1" disabled={this.state.reprintingId != null} onClick={() => this.reprint(o.id)}>Reimprimir</button>
                                                        {o.status !== 'cancelled' && (
                                                            <button type="button" className="btn btn-sm btn-outline-danger ml-1" onClick={() => this.askCancelSale(o.purchase_id, o.status === 'delivered')}>Anular venta</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {this.state.totalItems > 0 && (
                                        <div className="p-3">
                                            <PaginationComponent
                                                totalItems={this.state.totalItems}
                                                quantityRows={this.state.quantityShowRows}
                                                currentPage={this.state.currentPage}
                                                onPageChange={(page) => this.fetchOrders({ page })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
                {this.renderCancelModal()}
            </div>
        );
    }
}

export default CafeteriaOrdersPage;
